import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { AddressFieldMaxLength } from "../../../../../address-fields/AddressFields.constants";
import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { insuranceNumberValidation } from "../../../../../client-insurance-number/ClientInsuranceNumberField.utils";
import { ClientPersonalDataMaxLength } from "../../../../../constants/clientPersonalDataMaxLength";
import { isValidIban } from "../../../../../formik/formik-iban-field/FormikIbanField.utils";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { useDropdownOptions } from "../../../../../hooks/useDropdownOptions";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { ZipCodePattern } from "../../../../../utils/validationPatterns";
import { SaveAssetPersonalDataRequest } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { useSaveAttempted } from "../../../hooks/useSaveAttempted";
import { AssetInventoryMaxLength, AssetPersonalDataFormValues } from "../AssetPersonalData.types";

export function useAssetPersonalData(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  // Optional slice: without it the bank-statement upload block does not render, so no files can
  // ever be pending here (the guard below is defensive only).
  const assetInventoryDocumentsApi = useOptionalAssetInventoryDocumentsApi();

  const dropdownOptions = useDropdownOptions();

  const moveDocuments = useAssetDocumentMoves(clientId);

  const uploadBankStatements = async (files: File[]): Promise<File[]> => {
    if (!assetInventoryDocumentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await assetInventoryDocumentsApi.uploadAssetInventoryBankStatement(
            { clientId },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryBankStatements, clientId] });
    return failed;
  };

  const { data } = useQuery({
    queryKey: [ClientFileQueryKey.AssetInventory, clientId],
    queryFn: () => clientsApi.getClientAssetPersonalData({ clientId }),
  });

  const personalData = data?.personalData;
  const section1 = data?.assetInventory;

  const saveMutation = useMutation({
    mutationFn: (request: SaveAssetPersonalDataRequest) =>
      clientsApi.saveClientAssetPersonalData({ clientId, saveAssetPersonalDataRequest: request }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventory, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientPersonalData, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.Client, clientId] });
      showSuccess(t("clients.client.debtSettlement.assetInventory.section1.successfullySaved"));
    },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        salutation: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("clients.create.salutation") })
        ),
        title: Yup.string()
          .nullable()
          .max(ClientPersonalDataMaxLength.title, ({ max }) => t("common.validation.maxLength", { max })),
        firstName: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.name") }))
          .max(ClientPersonalDataMaxLength.firstName, ({ max }) => t("common.validation.maxLength", { max })),
        lastName: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.lastName") }))
          .max(ClientPersonalDataMaxLength.lastName, ({ max }) => t("common.validation.maxLength", { max })),
        formerLastName: Yup.string()
          .nullable()
          .max(ClientPersonalDataMaxLength.formerLastName, ({ max }) => t("common.validation.maxLength", { max })),
        placeOfBirth: Yup.string()
          .nullable()
          .max(ClientPersonalDataMaxLength.placeOfBirth, ({ max }) => t("common.validation.maxLength", { max })),
        street: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("common.address.street") }))
          .max(ClientPersonalDataMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
        houseNumber: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("common.address.houseNumber") }))
          .max(AddressFieldMaxLength.houseNumber, ({ max }) => t("common.validation.maxLength", { max })),
        zipCode: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("common.address.zipCode") }))
          .matches(ZipCodePattern, t("clients.create.invalidZipCode"))
          .min(4, ({ min }) => t("common.validation.minLength", { min }))
          .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
        city: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("common.address.city") }))
          .max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
        country: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("common.address.country") }))
          .max(ClientPersonalDataMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
        employmentStatus: Yup.string().required(
          t("clients.client.debtSettlement.assetInventory.section1.berufRequired")
        ),
        hasOwnAccount: Yup.boolean()
          .nullable()
          .required(
            t("common.validation.fieldRequired", {
              fieldName: t("clients.client.debtSettlement.assetInventory.section1.hasOwnAccountQuestion"),
            })
          ),
        causesDescription: Yup.string()
          .nullable()
          .max(AssetInventoryMaxLength.causesDescription, ({ max }) => t("common.validation.maxLength", { max })),
        phoneNumber: Yup.string()
          .nullable()
          .max(AssetInventoryMaxLength.phoneNumber, ({ max }) => t("common.validation.maxLength", { max })),
        accounts: Yup.array().when("hasOwnAccount", {
          is: true,
          then: (schema) =>
            schema.of(
              Yup.object().shape({
                iban: Yup.string()
                  .nullable()
                  .test("iban", t("common.iban.invalid"), (value) => !value || isValidIban(value)),
                incomings: Yup.string()
                  .nullable()
                  .required(t("clients.client.debtSettlement.assetInventory.section1.eingaengeRequired"))
                  .max(AssetInventoryMaxLength.incomings, ({ max }) => t("common.validation.maxLength", { max })),
                bankName: Yup.string()
                  .nullable()
                  .max(AssetInventoryMaxLength.bankName, ({ max }) => t("common.validation.maxLength", { max })),
                bankStreet: Yup.string()
                  .nullable()
                  .max(AddressFieldMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
                bankHouseNumber: Yup.string()
                  .nullable()
                  .max(AddressFieldMaxLength.houseNumber, ({ max }) => t("common.validation.maxLength", { max })),
                bankZipCode: Yup.string()
                  .nullable()
                  .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
                bankCity: Yup.string()
                  .nullable()
                  .max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
                bankCountry: Yup.string()
                  .nullable()
                  .max(AddressFieldMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
              })
            ),
        }),
        ...insuranceNumberValidation(t),
      }),
    [t]
  );

  const initialValues: AssetPersonalDataFormValues = useMemo(
    () => ({
      salutation: personalData?.salutation ?? "",
      title: personalData?.title ?? null,
      firstName: personalData?.firstName ?? "",
      lastName: personalData?.lastName ?? "",
      formerLastName: personalData?.formerLastName ?? null,
      insuranceNumber: personalData?.insuranceNumber ?? null,
      birthday: personalData?.birthday ?? null,
      placeOfBirth: personalData?.placeOfBirth ?? null,
      street: personalData?.street ?? "",
      houseNumber: personalData?.houseNumber ?? "",
      zipCode: personalData?.zipCode ?? "",
      city: personalData?.city ?? "",
      country: personalData?.country ?? "",
      employmentStatus: personalData?.employmentStatus ?? "",
      phoneNumber: section1?.phoneNumber ?? null,
      causesDescription: section1?.causesDescription ?? null,
      hasOwnAccount: section1?.hasOwnAccount ?? null,
      bankId: null,
      // The API groups the bank address into `address`; the form keeps it flat so each input has its
      // own Formik field. Flatten here, re-nest in onSubmit — the mapping lives on this boundary only.
      accounts: (section1?.accounts ?? []).map((a, i, arr) => ({
        id: a.id,
        bankName: a.bankName ?? null,
        bankStreet: a.address.street ?? null,
        bankHouseNumber: a.address.houseNumber ?? null,
        bankZipCode: a.address.zipCode ?? null,
        bankCity: a.address.city ?? null,
        bankCountry: a.address.country ?? null,
        iban: a.iban ?? null,
        incomings: a.incomings ?? null,
        isOverdrawn: a.isOverdrawn ?? null,
        hasFurtherAccount: i < arr.length - 1 ? true : arr.length === 1 ? (section1?.hasFurtherAccount ?? null) : null,
      })),
      bankStatements: [],
      documentMoves: [],
    }),
    [personalData, section1]
  );

  const formik = useFormikWithDirtyForm<AssetPersonalDataFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const hasOwnAccount = values.hasOwnAccount === true;
      const request: SaveAssetPersonalDataRequest = {
        personalData: {
          salutation: values.salutation || null,
          title: values.title,
          firstName: values.firstName,
          lastName: values.lastName,
          formerLastName: values.formerLastName,
          insuranceNumber: values.insuranceNumber,
          birthday: values.birthday,
          placeOfBirth: values.placeOfBirth,
          street: values.street,
          houseNumber: values.houseNumber,
          zipCode: values.zipCode,
          city: values.city,
          country: values.country,
          employmentStatus: values.employmentStatus || null,
        },
        assetInventory: {
          causesDescription: values.causesDescription,
          phoneNumber: values.phoneNumber,
          hasOwnAccount: values.hasOwnAccount,
          hasFurtherAccount: hasOwnAccount ? (values.accounts[0]?.hasFurtherAccount ?? null) : null,
          accounts: hasOwnAccount
            ? values.accounts.map((a) => ({
                id: a.id,
                bankName: a.bankName,
                address: {
                  street: a.bankStreet,
                  houseNumber: a.bankHouseNumber,
                  zipCode: a.bankZipCode,
                  city: a.bankCity,
                  country: a.bankCountry,
                },
                iban: a.iban,
                incomings: a.incomings,
                isOverdrawn: a.isOverdrawn,
              }))
            : [],
        },
      };

      await saveMutation.mutateAsync(request);

      if (values.bankStatements.length > 0) {
        const failed = await uploadBankStatements(values.bankStatements);
        await helpers.setFieldValue("bankStatements", failed);
      }

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue("documentMoves", failedMoves);
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const saveAttempted = useSaveAttempted(formik.submitCount);

  return { formik, dropdownOptions, saveAttempted, onAddMove, onCancelMove };
}
